import React from 'react';
import { Row, Col, Card, Table, ProgressBar } from 'react-bootstrap';
import { DealDetail } from '../../services/dealService';
import dealService from '../../services/dealService';

interface DealFinancialsProps {
  deal: DealDetail;
}

const DealFinancials: React.FC<DealFinancialsProps> = ({ deal }) => {
  // Calculate different ROI scenarios
  const calculateScenario = (purchasePrice: number, renoMultiplier: number = 1) => {
    const reno = deal.reno_estimate * renoMultiplier;
    const holding = deal.holding_costs || (purchasePrice * 0.008 * 4);
    const selling = deal.renovated_arv * 0.06;
    const totalInvestment = purchasePrice + reno + holding + selling;
    const profit = deal.renovated_arv - totalInvestment;
    const roi = (profit / totalInvestment) * 100;
    
    return {
      purchasePrice,
      reno,
      holding,
      selling,
      totalInvestment,
      profit,
      roi
    };
  };

  const scenarios = [
    { 
      name: 'List Price', 
      ...calculateScenario(deal.list_price)
    },
    { 
      name: 'MAO @ 20%', 
      ...calculateScenario(deal.mao_20pct)
    },
    { 
      name: 'MAO @ 15%', 
      ...calculateScenario(deal.mao_15pct || deal.mao_20pct * 1.05)
    }
  ];

  const maoGapPercent = Math.abs(deal.mao_gap_pct);
  const isGoodDeal = deal.mao_gap_pct <= 0;

  return (
    <>
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Property Valuation</h5>
            </Card.Header>
            <Card.Body>
              <Table striped className="mb-0">
                <tbody>
                  <tr>
                    <td>Current List Price</td>
                    <td className="text-end fw-bold">
                      {dealService.formatCurrency(deal.list_price)}
                    </td>
                  </tr>
                  <tr>
                    <td>As-Is Value (Based on Comps)</td>
                    <td className="text-end">
                      {dealService.formatCurrency(deal.as_is_value)}
                    </td>
                  </tr>
                  <tr>
                    <td>After Repair Value (ARV)</td>
                    <td className="text-end text-success fw-bold">
                      {dealService.formatCurrency(deal.renovated_arv)}
                    </td>
                  </tr>
                  <tr>
                    <td>Price per Sq Ft (Current)</td>
                    <td className="text-end">
                      ${Math.round(deal.list_price / deal.living_area)}/sqft
                    </td>
                  </tr>
                  <tr>
                    <td>Price per Sq Ft (ARV)</td>
                    <td className="text-end">
                      ${Math.round(deal.renovated_arv / deal.living_area)}/sqft
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Maximum Allowable Offer (MAO)</h5>
            </Card.Header>
            <Card.Body>
              <Table striped className="mb-3">
                <tbody>
                  <tr>
                    <td>MAO @ 20% ROI Target</td>
                    <td className="text-end fw-bold">
                      {dealService.formatCurrency(deal.mao_20pct)}
                    </td>
                  </tr>
                  <tr>
                    <td>MAO @ 15% ROI Target</td>
                    <td className="text-end">
                      {dealService.formatCurrency(deal.mao_15pct || deal.mao_20pct * 1.05)}
                    </td>
                  </tr>
                  <tr>
                    <td>List Price</td>
                    <td className="text-end">
                      {dealService.formatCurrency(deal.list_price)}
                    </td>
                  </tr>
                  <tr>
                    <td>MAO Gap</td>
                    <td className={`text-end fw-bold ${isGoodDeal ? 'text-success' : 'text-danger'}`}>
                      {isGoodDeal ? '+' : '-'}{maoGapPercent.toFixed(1)}%
                    </td>
                  </tr>
                </tbody>
              </Table>

              <div className="mt-3">
                <small className="text-muted">MAO Gap Analysis</small>
                <ProgressBar className="mt-1">
                  <ProgressBar
                    variant={isGoodDeal ? 'success' : 'danger'}
                    now={Math.min(100, maoGapPercent)}
                    label={`${maoGapPercent.toFixed(0)}%`}
                  />
                </ProgressBar>
                <small className={`mt-1 ${isGoodDeal ? 'text-success' : 'text-danger'}`}>
                  {isGoodDeal 
                    ? '✓ List price is below MAO - Good deal opportunity!' 
                    : '✗ List price exceeds MAO - Negotiate or pass'}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>
          <h5 className="mb-0">Flip Economics Scenarios</h5>
        </Card.Header>
        <Card.Body>
          <Table responsive>
            <thead>
              <tr>
                <th>Scenario</th>
                <th className="text-end">Purchase</th>
                <th className="text-end">Renovation</th>
                <th className="text-end">Holding</th>
                <th className="text-end">Selling</th>
                <th className="text-end">Total Cost</th>
                <th className="text-end">ARV</th>
                <th className="text-end">Profit</th>
                <th className="text-end">ROI</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((scenario, index) => (
                <tr key={index} className={scenario.name === 'List Price' ? 'table-active' : ''}>
                  <td className="fw-bold">{scenario.name}</td>
                  <td className="text-end">{dealService.formatCurrency(scenario.purchasePrice)}</td>
                  <td className="text-end">{dealService.formatCurrency(scenario.reno)}</td>
                  <td className="text-end">{dealService.formatCurrency(scenario.holding)}</td>
                  <td className="text-end">{dealService.formatCurrency(scenario.selling)}</td>
                  <td className="text-end fw-bold">{dealService.formatCurrency(scenario.totalInvestment)}</td>
                  <td className="text-end">{dealService.formatCurrency(deal.renovated_arv)}</td>
                  <td className={`text-end fw-bold ${scenario.profit > 0 ? 'text-success' : 'text-danger'}`}>
                    {dealService.formatCurrency(scenario.profit)}
                  </td>
                  <td className={`text-end fw-bold ${scenario.roi >= 15 ? 'text-success' : scenario.roi >= 10 ? 'text-warning' : 'text-danger'}`}>
                    {scenario.roi.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Row className="mt-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Renovation Breakdown</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Renovation Tier: <span className="fw-bold text-capitalize">medium</span>
              </p>
              <Table size="sm">
                <tbody>
                  <tr>
                    <td>Base Renovation Cost</td>
                    <td className="text-end">{dealService.formatCurrency(deal.reno_estimate)}</td>
                  </tr>
                  <tr>
                    <td>Cost per Sq Ft</td>
                    <td className="text-end">${Math.round(deal.reno_estimate / deal.living_area)}/sqft</td>
                  </tr>
                  <tr>
                    <td>10% Contingency</td>
                    <td className="text-end">{dealService.formatCurrency(deal.reno_estimate * 0.1)}</td>
                  </tr>
                  <tr className="fw-bold">
                    <td>Total with Contingency</td>
                    <td className="text-end">{dealService.formatCurrency(deal.reno_estimate * 1.1)}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Investment Summary</h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-3">
                <h2 className="mb-0 text-primary">{dealService.formatROI(deal.flip_roi)}</h2>
                <p className="text-muted">Expected Return on Investment</p>
              </div>
              
              <div className="d-flex justify-content-around text-center">
                <div>
                  <h5 className="mb-0">{dealService.formatCurrency(deal.flip_profit)}</h5>
                  <small className="text-muted">Net Profit</small>
                </div>
                <div>
                  <h5 className="mb-0">{deal.flip_grade}</h5>
                  <small className="text-muted">Deal Grade</small>
                </div>
                <div>
                  <h5 className="mb-0">4-6mo</h5>
                  <small className="text-muted">Timeline</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default DealFinancials;